import { createElement, type HTMLAttributes, type ReactNode } from 'react';
import { Button } from '../Button';
import { useDialogRef } from './dialogRefContext';

/** `<mat-dialog-content>`. */
export function DialogContent({ className = '', ...rest }: HTMLAttributes<HTMLElement>) {
  return createElement('mat-dialog-content', {
    className: `mat-mdc-dialog-content mdc-dialog__content ${className}`.trim(),
    ...rest,
  });
}

/** `<mat-dialog-actions>`. */
export function DialogActions({ className = '', ...rest }: HTMLAttributes<HTMLElement>) {
  return createElement('mat-dialog-actions', {
    className: `mat-mdc-dialog-actions mdc-dialog__actions ${className}`.trim(),
    ...rest,
  });
}

/**
 * `mat-dialog-close` / `[mat-dialog-close]="value"`: clicking closes the dialog with `result`.
 * Renders a plain element, so give it the same class names the Angular markup had.
 */
export function DialogClose<R>({
  as: Tag = 'div',
  result,
  children,
  ...rest
}: {
  as?: 'div' | 'button';
  result?: R;
  children: ReactNode;
} & Omit<HTMLAttributes<HTMLElement>, 'onClick'> & { type?: 'button' }) {
  const ref = useDialogRef<R>();
  // `<button mat-button mat-dialog-close>` is a Material button (ripple, label, touch target)
  if (Tag === 'button') {
    return (
      <Button {...rest} onClick={() => ref.close(result)}>
        {children}
      </Button>
    );
  }
  return (
    <Tag {...rest} className={`cursor-pointer ${rest.className ?? ''}`.trim()} onClick={() => ref.close(result)}>
      {children}
    </Tag>
  );
}
